import { useEffect, useState } from "react"
import { useAppSelector } from "../app/hooks"
import { CommonErrorText } from "../common/errorText"
import { FlightText } from "../common/text"

import { IDateFieldProperties } from "./generic/form/DatePickerField"
import Tooltip from "./generic/tooltip"

import SeasonDatePicker from "./SeasonDatePicker"

interface Props extends IDateFieldProperties {
  dateRangeStartValue: string
  dateRangeEndValue: string
  showCaption?: boolean
  readonly?: boolean
  hasChanges?: boolean
  onChange: (
    name: string,
    value: string,
    isValid?: boolean,
    callback?: () => void
  ) => void
  required?: boolean
  labelOnBorder?: boolean
  placeholder?: string
  isAddPage?: boolean
  customStartName?: string
  customEndName?: string
}

const SeasonDateRange: React.FunctionComponent<Props> = ({
  dateRangeStartValue,
  dateRangeEndValue,
  dropdownId,
  showCaption,
  hasChanges,
  autoFocus,
  readonly,
  onChange,
  touched,
  required,
  showIcon,
  placement,
  labelOnBorder,
  placeholder,
  errors,
  hasError,
  isAddPage,
  dateHasErrText,
  customEndName,
  customStartName
}) => {
  const { useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}
  const [hasSelectedStartDate, setHasSelectedStartDate] = useState(false)
  const showDivider = readonly ? dateRangeStartValue || dateRangeEndValue : true
  const showStartAsterisk =
    readonly && !dateRangeStartValue && dateRangeEndValue
  const showEndAsterisk = readonly && dateRangeStartValue && !dateRangeEndValue

  const asterisk = <p className="ocs-field-readonly">*</p>

  useEffect(() => {
    setHasSelectedStartDate(false)
    if (hasSelectedStartDate && !dateRangeEndValue) {
      onChange(customEndName ?? "endDate", dateRangeStartValue, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelectedStartDate])

  return (
    <div
      className={
        "ocs-range-field pos-rel" +
        (readonly && hasChanges
          ? useLocalTime
            ? " has-changes local-time"
            : " has-changes"
          : "") +
        (labelOnBorder ? " label-on-border" : "")
      }
    >
      <div className="d-flex">
        {showCaption && <label>{FlightText.dateRange}</label>}

        {((errors && errors.dateRangeError) || hasError) && (
          <Tooltip>{CommonErrorText.startdateGreater}</Tooltip>
        )}
      </div>
      <div className="ocs-range">
        <SeasonDatePicker
          required={required}
          isAddPage={isAddPage}
          readonly={readonly}
          name={customStartName ?? "startDate"}
          value={dateRangeStartValue}
          isEmpty={
            required &&
            (dateRangeStartValue === null || dateRangeStartValue === "")
          }
          hasError={hasError}
          onChange={(name: string, val: string, error: boolean) => {
            onChange(name, val, error)
          }}
          onSelectDate={(newDate) =>
            newDate !== null && setHasSelectedStartDate(true)
          }
          dropdownId={dropdownId + "-start"}
          autoFocus={autoFocus}
          placement={placement}
          touched={touched}
          placeholder={placeholder}
          dateHasErrText={dateHasErrText}
        />
        {showStartAsterisk && asterisk}
        {showDivider && (
          <div className={!readonly ? "ocs-range-field-divider" : ""}>
            <p
              className={
                readonly
                  ? "ocs-field-readonly ocs-range-field-divider-readonly"
                  : ""
              }
            >
              -
            </p>
          </div>
        )}
        {showEndAsterisk && asterisk}
        <div
          className={
            !readonly ? "ocs-range-field-last" : "ocs-range-field-last-readonly"
          }
        >
          <SeasonDatePicker
            required={required}
            isAddPage={isAddPage}
            readonly={readonly}
            name={customEndName ?? "endDate"}
            value={dateRangeEndValue}
            onChange={onChange}
            showIcon={showIcon}
            dropdownId={dropdownId + "-end"}
            placement={placement}
            touched={touched}
            isEmpty={
              required &&
              (dateRangeEndValue === null || dateRangeEndValue === "")
            }
          />
        </div>
      </div>
    </div>
  )
}

SeasonDateRange.defaultProps = {
  showCaption: true,
  showIcon: true,
  readonly: false
}

export default SeasonDateRange
